import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { StreamsContext } from '../../contexts/StreamsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import useIsMobile from '../../hooks/useIsMobile';
import { useUpdateAppSettings } from '../../hooks/useBackend';
import { MOBILE_UI } from '../../constants/mobileUi';
import MultiLineChart, { MARKET_INDEX_SYMBOLS } from '../StockChart/MultiLineChart';
import { SectionTitleWithControl } from '../shared/TimeframeSelect';
import SegmentedControl from '../shared/SegmentedControl';
import IndexComparisonChartSettings from '../MarketOverview/IndexComparisonChartSettings';
import MarketStrengthOdometer from '../MarketOverview/MarketStrengthOdometer';
import { netChangePctToDisplayPercent, parseQuoteLast } from '../../utils/quoteNetChangePct';
import { navigateToTradeWithSymbol } from '../../utils/tradeNavigation';
import { MARKET_INDEX_CONFIG, MARKET_OVERVIEW_TIME_FRAMES } from '../../constants/marketIndices';
import { readStoredPeriod, writeStoredPeriod } from '../../utils/reviewPeriodStorage';
import {
  MARKET_OVERVIEW_CHART_STYLE_KEY,
  normalizeMarketOverviewChartStyle,
} from '../../constants/marketOverviewChartStyle';

function IndexQuoteCards({ columns = 3, compact = false }) {
  const { colors } = useTheme();
  const { quotesBySymbol } = useContext(StreamsContext);
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const navigate = useNavigate();

  const openTrade = useCallback((symbol) => {
    navigateToTradeWithSymbol(navigate, symbol, user, selectedAccount);
  }, [navigate, user, selectedAccount]);

  return (
    <div
      data-testid="market-overview-index-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: compact ? MOBILE_UI.gap : 12,
      }}
    >
      {MARKET_INDEX_SYMBOLS.map((symbol) => {
        const quote = quotesBySymbol[symbol] || quotesBySymbol[String(symbol).toUpperCase()];
        const price = parseQuoteLast(quote);
        const change = quote?.NetChange == null ? NaN : parseFloat(quote.NetChange);
        const changePct = netChangePctToDisplayPercent(quote);
        const hasQuote = Number.isFinite(price);
        const hasChange = Number.isFinite(change) && Number.isFinite(changePct);
        const displayName = MARKET_INDEX_CONFIG[symbol]?.name || symbol;

        return (
          <div
            key={symbol}
            data-testid={`index-card-${symbol}`}
            style={{
              padding: compact ? MOBILE_UI.gap : 16,
              backgroundColor: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: compact ? MOBILE_UI.radiusCard : 10,
            }}
          >
            <button
              type="button"
              data-testid={`index-ticker-${symbol}`}
              onClick={() => openTrade(symbol)}
              title={`Open ${symbol} on Trade`}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 0,
                marginBottom: compact ? 2 : 8,
                textAlign: 'left',
                fontSize: compact ? 14 : 15,
                fontWeight: 700,
                color: colors.primary,
                cursor: 'pointer',
                textDecoration: 'none',
              }}
            >
              {symbol}
            </button>
            <div
              style={{
                fontSize: compact ? MOBILE_UI.font.meta : 12,
                color: colors.textMuted,
                marginBottom: 6,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayName}
            </div>
            <div
              style={{
                fontSize: compact ? 18 : 20,
                fontWeight: 700,
                marginBottom: compact ? 2 : 4,
                fontFamily: 'monospace',
                color: colors.text,
              }}
            >
              {hasQuote ? `$${price.toFixed(2)}` : '—'}
            </div>
            <div
              style={{
                fontSize: compact ? MOBILE_UI.font.body : 14,
                fontWeight: 500,
                color: !hasChange ? colors.textMuted : change >= 0 ? colors.success : colors.danger,
              }}
            >
              {hasChange
                ? `${changePct >= 0 ? '+' : ''}${changePct.toFixed(2)}% (${change >= 0 ? '+' : ''}${change.toFixed(2)})`
                : '—'}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SectionHeading({ children }) {
  const { colors } = useTheme();
  return (
    <div
      style={{
        fontSize: 16,
        fontWeight: 700,
        color: colors.text,
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

const MARKET_OVERVIEW_TF_OPTIONS = MARKET_OVERVIEW_TIME_FRAMES.map((tf) => ({
  value: tf.key,
  label: tf.label || tf.key,
}));

const TIMEFRAME_CONTROL_OPTIONS = MARKET_OVERVIEW_TF_OPTIONS.map((tf) => ({
  ...tf,
  testId: `index-comparison-timeframe-${tf.value}`,
}));

const MARKET_OVERVIEW_PERIOD_KEY = 'pt:reviewPeriod:marketOverview';
const MARKET_OVERVIEW_PERIOD_IDS = MARKET_OVERVIEW_TIME_FRAMES.map((tf) => tf.key);

/** Market Overview: Indices cards + Index comparison chart. */
const MarketOverviewPage = () => {
  const isMobile = useIsMobile();
  const { user } = useContext(AuthContext);
  const { updateAppSettings } = useUpdateAppSettings();
  const [timeframe, setTimeframe] = useState(() => (
    readStoredPeriod(MARKET_OVERVIEW_PERIOD_KEY, '1D', MARKET_OVERVIEW_PERIOD_IDS)
  ));
  const handleTimeframeChange = useCallback((next) => {
    setTimeframe(next);
    writeStoredPeriod(MARKET_OVERVIEW_PERIOD_KEY, next, MARKET_OVERVIEW_PERIOD_IDS);
  }, []);
  const savedChartStyle = user?.appSettings?.[MARKET_OVERVIEW_CHART_STYLE_KEY];

  const chartStyle = useMemo(
    () => normalizeMarketOverviewChartStyle(savedChartStyle),
    [savedChartStyle],
  );

  const handleChartStyleChange = useCallback(async (nextStyle) => {
    const normalized = normalizeMarketOverviewChartStyle(nextStyle);
    if (!user?.token) return;
    try {
      await updateAppSettings(user.token, {
        [MARKET_OVERVIEW_CHART_STYLE_KEY]: normalized,
      });
    } catch (err) {
      console.error('Failed to save index comparison chart style:', err);
    }
  }, [user, updateAppSettings]);

  const marketSummarySection = (
    <div
      data-testid="market-overview-summary-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 2fr) minmax(240px, 1fr)',
        gap: isMobile ? MOBILE_UI.sectionGap : 16,
        flexShrink: 0,
      }}
    >
      <section data-testid="market-overview-indices-section">
        <SectionHeading>Indices</SectionHeading>
        {/* Three cards per row leaves ~80px of content per card on a phone — price and change clip. */}
        <IndexQuoteCards columns={isMobile ? 2 : 3} compact={isMobile} />
      </section>
      <section data-testid="market-overview-strength-section">
        <SectionHeading>Market strength</SectionHeading>
        <MarketStrengthOdometer height={isMobile ? 176 : 256} />
      </section>
    </div>
  );

  const chartSettings = (
    <IndexComparisonChartSettings
      lineWidth={chartStyle.lineWidth}
      lineStyle={chartStyle.lineStyle}
      onChange={handleChartStyleChange}
    />
  );

  const chartSection = (
    <section
      data-testid="market-overview-chart-section"
      style={{
        flex: isMobile ? undefined : 1,
        minHeight: isMobile ? undefined : 0,
        minWidth: 0,
        ...(isMobile ? null : { display: 'flex', flexDirection: 'column' }),
      }}
    >
      <SectionTitleWithControl
        title="Index comparison"
        titleTestId="index-comparison-header"
        style={{ flexShrink: 0 }}
        marginBottom={isMobile ? 8 : 10}
        trailing={chartSettings}
      />
      <div style={{ marginBottom: 10 }}>
        <SegmentedControl
          options={TIMEFRAME_CONTROL_OPTIONS}
          value={timeframe}
          onChange={handleTimeframeChange}
          size="sm"
          scrollable
          ariaLabel="Timeframe"
          testId="index-comparison-timeframe"
        />
      </div>
      <MultiLineChart
        symbols={MARKET_INDEX_SYMBOLS}
        height={isMobile ? 360 : 320}
        fillHeight={!isMobile}
        timeframe={timeframe}
        lineWidth={chartStyle.lineWidth}
        lineStyle={chartStyle.lineStyle}
      />
    </section>
  );

  if (isMobile) {
    return (
      <div
        data-testid="market-overview-mobile"
        style={{
          // MarketPage's shell owns the bottom-nav clearance; this is just trailing breathing room.
          padding: MOBILE_UI.pagePadding,
          display: 'flex',
          flexDirection: 'column',
          gap: MOBILE_UI.sectionGap,
        }}
      >
        {marketSummarySection}
        {chartSection}
      </div>
    );
  }

  return (
    <div
      data-testid="market-overview-desktop"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        gap: 16,
      }}
    >
      {marketSummarySection}
      {chartSection}
    </div>
  );
};

export default MarketOverviewPage;
